import '@fontsource-variable/ibm-plex-sans/wght.css';
import './styles/tokens.css';
import './styles/app.css';
import './styles/refresh.css';
import './styles/call-frame.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App.tsx';
import { RouterProvider } from './lib/router.tsx';
import { AnnouncerProvider } from './ui/Announcer.tsx';
import { applyTheme, getThemePreference } from './lib/theme.ts';
import { APP_NAME } from './config/brand.ts';
import { createBrowserBridge } from './call/browserEngine.ts';
import { detectDesktopBridge } from './desktop/bridge.ts';
import { FragmentVault } from './identity/fragment.ts';
import { captureCallLaunches } from './call/launchIntents.ts';
import { captureInstallPrompt } from './lib/install.ts';
import { parseCallWindowLocation } from './features/callWindow/callWindowLaunch.ts';
import { CallWindowRoot } from './features/callWindow/CallWindowRoot.tsx';

// The call window (/call-window/<call id> or /call-window/pending?launch=<nonce>) is its own root, chosen before App:
// no workspace, no phone, no call-link redirect, no install prompt. Everything else is the app.
const callWindow = parseCallWindowLocation(window.location.pathname, window.location.search);

let fragments: FragmentVault | null = null;
if (!callWindow) {
  captureCallLaunches();
  captureInstallPrompt();

  // /invite#token=… and /recover#token=…: scrub the fragment from the address bar before anything
  // renders or fetches, and keep the token only in memory for its page. A second link opened in
  // this tab arrives as a hashchange and is scrubbed the same way.
  const vault = new FragmentVault();
  vault.capture(window.location, window.history);
  window.addEventListener('hashchange', () => vault.capture(window.location, window.history));
  fragments = vault;
}

applyTheme(getThemePreference());
document.title = callWindow ? `Call window - ${APP_NAME}` : APP_NAME;

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');
createRoot(root).render(
  <StrictMode>
    <AnnouncerProvider>
      {callWindow ? (
        <CallWindowRoot target={callWindow} />
      ) : (
        <RouterProvider>
          <App bridgeFactory={createBrowserBridge} desktop={detectDesktopBridge()} {...(fragments ? { fragments } : {})} />
        </RouterProvider>
      )}
    </AnnouncerProvider>
  </StrictMode>,
);
